'use client';

import type React from 'react';

import {
    MenuItemPicker,
    SelectedItemsList,
    type SelectedItem,
} from '@/components/menu-item-picker';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';


import { Textarea } from '@/components/ui/textarea';
import { router, useForm } from '@inertiajs/react';
import { Clock, Loader2, Mail, MessageSquare, Phone, User } from 'lucide-react';
import { useState } from 'react';
import { InputGroup, InputGroupAddon, InputGroupInput } from './ui/input-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface OrderRequestFormProps {
    menuItems: App.Data.MenuItemData[];
    categories: Array<string>;
    customer?: App.Data.CustomerData | null;
}

export function OrderRequestForm({
    menuItems,
    categories,
    customer,
}: OrderRequestFormProps) {
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [successOpen, setSuccessOpen] = useState(false);
    const form = useForm({
        name: customer?.name || '',
        email: customer?.email || '',
        phone: customer?.phone || '',
        arrival_time: '',
        notes: '',
        items: [] as Array<{ id: number; quantity: number }>,
        title: 'Order Request',
    });

    const generateTimeSlots = () => {
        const slots: string[] = [];
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        for (let hour = 0; hour < 24; hour++) {
            for (const minute of [0, 30]) {
                if (
                    hour > currentHour ||
                    (hour === currentHour && minute > currentMinute)
                ) {
                    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                    slots.push(time);
                }
            }
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedItems.length === 0) {
            return;
        }

        form.transform((data) => ({
            ...data,
            items: selectedItems.map((si) => ({
                id: Number(si.item.id),
                quantity: si.quantity,
            })),
        }));

        form.post('/request', {
            onSuccess: () => {
                setSelectedItems([]);
                form.reset();

                setTimeout(() => {
                    router.visit('/');
                }, 2500);
            },
            onError: (errors) => {
                console.error('Form errors:', errors);
            },
        });
    };

    const totalPrice = selectedItems.reduce(
        (sum, si) => sum + si.item.price * si.quantity,
        0,
    );

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Step 1: Select Items */}
                <Card className="border-2">
                    <CardHeader className="bg-muted/30">
                        <CardTitle className="flex items-center gap-2 text-xl">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                                1
                            </span>
                            Select Your Items
                        </CardTitle>
                        <CardDescription>
                            Browse our menu and add items to your order
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <MenuItemPicker
                            selectedItems={selectedItems}
                            onItemsChange={setSelectedItems}
                            menuItems={menuItems}
                            categories={categories}
                        />
                        <SelectedItemsList
                            selectedItems={selectedItems}
                            onItemsChange={setSelectedItems}
                        />
                    </CardContent>
                </Card>

                {/* Step 2: Pickup Details */}
                <Card className="border-2">
                    <CardHeader className="bg-muted/30">
                        <CardTitle className="flex items-center gap-2 text-xl">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                                2
                            </span>
                            Pickup Details
                        </CardTitle>
                        <CardDescription>
                            Select when you'll arrive to pick up your order
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-6">
                        {/* Common Fields */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label
                                    htmlFor="name"
                                    className="flex items-center gap-2"
                                >
                                    <User className="h-4 w-4" />
                                    Full Name
                                </Label>
                                <Input
                                    id="name"
                                    placeholder="John Doe"
                                    value={form.data.name}
                                    onChange={(e) =>
                                        form.setData('name', e.target.value)
                                    }
                                    required
                                />
                                {form.errors.name && (
                                    <p className="text-sm text-destructive">
                                        {form.errors.name}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="phone"
                                    className="flex items-center gap-2"
                                >
                                    <Phone className="h-4 w-4" />
                                    Phone Number
                                </Label>
                                <InputGroup>
                                    <InputGroupAddon>+20</InputGroupAddon>
                                    <InputGroupInput
                                        id="phone"
                                        type="tel"
                                        placeholder="+1 (555) 000-0000"
                                        value={form.data.phone}
                                        onChange={(e) =>
                                            form.setData(
                                                'phone',
                                                e.target.value,
                                            )
                                        }
                                        required
                                    />
                                </InputGroup>
                                {form.errors.phone && (
                                    <p className="text-sm text-destructive">
                                        {form.errors.phone}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label
                                htmlFor="email"
                                className="flex items-center gap-2"
                            >
                                <Mail className="h-4 w-4" />
                                Email Address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="john@example.com"
                                value={form.data.email}
                                onChange={(e) =>
                                    form.setData('email', e.target.value)
                                }
                            />
                            {form.errors.email && (
                                <p className="text-sm text-destructive">
                                    {form.errors.email}
                                </p>
                            )}
                        </div>

                        {/* Pickup-specific fields */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="pickupTime"
                                className="flex items-center gap-2"
                            >
                                <Clock className="h-4 w-4" />
                                Arrival Time
                            </Label>
                            <Select
                                value={form.data.arrival_time}
                                onValueChange={(value) =>
                                    form.setData('arrival_time', value)
                                }
                                required
                            >
                                <SelectTrigger id="arrival_time">
                                    <SelectValue placeholder="Select your arrival time" />
                                </SelectTrigger>
                                <SelectContent>
                                    {timeSlots.length > 0 ? (
                                        timeSlots.map((time) => (
                                            <SelectItem key={time} value={time}>
                                                {time}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <SelectItem value="tomorrow" disabled>
                                            No slots available today - Please
                                            order for tomorrow
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                            {form.errors.arrival_time && (
                                <p className="text-sm text-destructive">
                                    {form.errors.arrival_time}
                                </p>
                            )}
                            <p className="text-sm text-muted-foreground">
                                We'll have your order ready when you arrive
                            </p>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="notes"
                                className="flex items-center gap-2"
                            >
                                <MessageSquare className="h-4 w-4" />
                                Special Instructions (Optional)
                            </Label>
                            <Textarea
                                id="notes"
                                placeholder="Any allergies, special requests, or delivery instructions..."
                                value={form.data.notes}
                                onChange={(e) =>
                                    form.setData('notes', e.target.value)
                                }
                                rows={3}
                            />
                            {form.errors.notes && (
                                <p className="text-sm text-destructive">
                                    {form.errors.notes}
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Order Summary & Submit */}
                <Card className="border-2 border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                    Subtotal
                                </span>
                                <span>${totalPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-3 text-lg font-bold">
                                <span>Total</span>
                                <span className="text-primary">
                                    ${totalPrice.toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            size="lg"
                            className="mt-6 h-14 w-full text-lg"
                            disabled={
                                selectedItems.length === 0 ||
                                !form.data.arrival_time ||
                                form.processing
                            }
                        >
                            {form.processing ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    Submitting...
                                </div>
                            ) : selectedItems.length === 0 ? (
                                'Select Items to Continue'
                            ) : (
                                'Place Order'
                            )}
                        </Button>
                        {form.errors.items && (
                            <p className="mt-2 text-center text-sm text-destructive">
                                {form.errors.items}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </form>
            <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
                <DialogContent className="text-center sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl">
                            Order Submitted 🎉
                        </DialogTitle>
                        <DialogDescription className="mt-2">
                            Thank you for your order.
                            <br />
                            We’ll contact you shortly to confirm.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-6 flex justify-center">
                        <Button
                            onClick={() => router.visit('/')}
                            className="w-full"
                        >
                            Go to Home Now
                        </Button>
                    </div>

                    <p className="mt-3 text-sm text-muted-foreground">
                        Redirecting automatically…
                    </p>
                </DialogContent>
            </Dialog>
        </>
    );
}
